import React, { useState, useEffect, useRef } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { format, parseISO } from 'date-fns';
import { getEndpointResponseTimes } from '../../../shared/api/publicApi';
import '../../../styles/features/status/ResponseTimeChart.css';

const PRESETS = [
  { label: '24h', hours: 24 },
  { label: '7d', days: 7 },
  { label: '30d', days: 30 },
];

function ResponseTimeChart({ endpointId }) {
  const [preset, setPreset] = useState(PRESETS[0]);
  const [chartData, setChartData] = useState([]);
  const [granularity, setGranularity] = useState('day');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const chartRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '100px' }
    );

    if (chartRef.current) {
      observer.observe(chartRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!endpointId || !isVisible) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    const params = preset.hours ? { hours: preset.hours } : { days: preset.days };

    getEndpointResponseTimes(endpointId, params)
      .then((res) => {
        if (cancelled) return;

        const g = res.data.granularity || 'day';
        setGranularity(g);

        const fmt = g === 'hour' ? 'HH:mm' : 'MMM d';

        const rows = (res.data.data || []).map((row) => ({
          time: format(parseISO(row.day), fmt),
          avg: row.avg,
          min: row.min,
          max: row.max,
          checks: row.checks,
        }));

        setChartData(rows);

        if (rows.length > 0) {
          const avgs = rows.map((r) => r.avg).filter((n) => n != null);
          const mins = rows.map((r) => r.min).filter((n) => n != null);
          const maxs = rows.map((r) => r.max).filter((n) => n != null);

          setStats({
            min: mins.length ? Math.min(...mins) : null,
            avg:
              avgs.length
                ? avgs.reduce((a, b) => a + b, 0) / avgs.length
                : null,
            max: maxs.length ? Math.max(...maxs) : null,
          });
        } else {
          setStats(null);
        }
      })
      .catch((err) => {
        if (!cancelled) setError('Failed to load response time data');
        console.error(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [endpointId, preset, isVisible]);

  if (!isVisible) {
    return (
      <div className="response-time-chart" ref={chartRef}>
        <div className="chart-empty">
          <p>Scroll to load chart data...</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="response-time-chart" ref={chartRef}>
        <div className="chart-empty">
          <p>Loading response time data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="response-time-chart" ref={chartRef}>
        <div className="chart-empty">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (chartData.length === 0) {
    return (
      <div className="response-time-chart" ref={chartRef}>
        <div className="chart-empty">
          <p>No checks recorded yet</p>
        </div>
      </div>
    );
  }

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    const d = payload[0]?.payload;

    return (
      <div className="rt-tooltip">
        <p className="rt-tooltip__label">{label}</p>
        <p className="rt-tooltip__row rt-tooltip__row--avg">
          Avg: <strong>{d?.avg}ms</strong>
        </p>
        <p className="rt-tooltip__row rt-tooltip__row--min">
          Min: <strong>{d?.min}ms</strong>
        </p>
        <p className="rt-tooltip__row rt-tooltip__row--max">
          Max: <strong>{d?.max}ms</strong>
        </p>
        <p className="rt-tooltip__checks">{d?.checks} checks</p>
      </div>
    );
  };

  return (
    <div className="response-time-chart" ref={chartRef}>
      <div className="response-time-chart__header">
        <div className="response-time-chart__heading">
          <div className="response-time-chart__title-group">
            <h3>Response Time</h3>
            <span className="response-time-chart__range">
              {granularity === 'hour' ? 'Hourly averages' : 'Daily averages'}
            </span>
          </div>

          {stats && (
            <div className="rt-stats-bar">
              {stats.min != null && (
                <span className="rt-stat-chip">
                  <span className="rt-stat-chip__label">Min</span>
                  <strong>{Math.round(stats.min)}ms</strong>
                </span>
              )}
              {stats.avg != null && (
                <span className="rt-stat-chip">
                  <span className="rt-stat-chip__label">Avg</span>
                  <strong>{Math.round(stats.avg)}ms</strong>
                </span>
              )}
              {stats.max != null && (
                <span className="rt-stat-chip">
                  <span className="rt-stat-chip__label">Max</span>
                  <strong>{Math.round(stats.max)}ms</strong>
                </span>
              )}
            </div>
          )}
        </div>

        <div className="response-time-chart__presets">
          {PRESETS.map((p) => (
            <button
              key={p.label}
              className={`rt-preset-btn${
                preset.label === p.label ? ' rt-preset-btn--active' : ''
              }`}
              onClick={() => setPreset(p)}
              type="button"
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <ResponsiveContainer width="100%" height={280}>
        <AreaChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="gradAvg" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#6366f1" stopOpacity={0.5} />
              <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="gradMax" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.25} />
              <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
            </linearGradient>
          </defs>

          <CartesianGrid
            strokeDasharray="3 3"
            stroke="rgba(255,255,255,0.06)"
            vertical={false}
          />

          <XAxis
            dataKey="time"
            stroke="var(--text-tertiary, #94a3b8)"
            tick={{ fontSize: 11 }}
            tickLine={false}
            axisLine={false}
          />

          <YAxis
            stroke="var(--text-tertiary, #94a3b8)"
            tick={{ fontSize: 11 }}
            tickLine={false}
            axisLine={false}
            tickFormatter={(v) => `${v}ms`}
            width={55}
          />

          <Tooltip content={<CustomTooltip />} />

          <Legend
            wrapperStyle={{ fontSize: '12px', paddingTop: '8px' }}
            formatter={(value) =>
              value === 'avg'
                ? 'Avg'
                : value === 'max'
                ? 'Max'
                : value === 'min'
                ? 'Min'
                : value
            }
          />

          <Area
            type="monotone"
            dataKey="max"
            stroke="#f59e0b"
            strokeWidth={1}
            strokeDasharray="4 3"
            fill="url(#gradMax)"
            dot={false}
            name="max"
          />

          <Area
            type="monotone"
            dataKey="avg"
            stroke="#6366f1"
            strokeWidth={2}
            fill="url(#gradAvg)"
            dot={false}
            activeDot={{ r: 5 }}
            name="avg"
          />

          <Area
            type="monotone"
            dataKey="min"
            stroke="#10b981"
            strokeWidth={1}
            fill="none"
            dot={false}
            name="min"
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

export default ResponseTimeChart;
